import React from 'react';
import { Mail, FileText, Globe, Languages } from 'lucide-react';
import profileJuho from '../../pictures/juho-songer.jpg';
import profileJesper from '../../pictures/jesper-songer-2.jpg';
const Team = ({ theme }) => {
  const textColor = theme === 'tech' ? 'text-green-400' : 'text-blue-500';

  const CompanyCard = ({ name, industry, url, theme }) => (
      <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`p-3 rounded-lg bg-white/5 block transition-all duration-200 
      hover:bg-white/10 hover:scale-105 hover:shadow-lg
      ${theme === 'tech' ? 'hover:ring-1 hover:ring-green-400/30' : 'hover:ring-1 hover:ring-blue-500/30'}`}
      >
    <span className="font-medium flex items-center gap-2">
      {name}
      <svg
          className={`w-4 h-4 opacity-50 transition-transform duration-200 transform group-hover:translate-x-1
          ${theme === 'tech' ? 'text-green-400' : 'text-blue-500'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </span>
        <span className="block opacity-70 text-sm">{industry}</span>
      </a>
  );

  return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-12 text-center">Our Team</h1>

        <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Profile Image Column */}
            <div className="md:w-1/3 flex flex-col items-center space-y-4">
              <div className="relative group">
                <img
                    src={profileJuho}
                    alt="Juho Songer"
                    className="w-64 h-64 rounded-2xl object-cover shadow-lg
                      transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 rounded-2xl ring-2 ring-offset-2 ring-offset-black/20
                ring-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
              </div>

              {/* Quick Contact Info */}
              <div className="w-full space-y-3 mt-6">
                <a href="mailto:juho.songer@songerconsulting.se"
                   className={`flex items-center gap-2 ${textColor} hover:opacity-80 transition-opacity`}>
                  <Mail size={18}/>
                  <span>juho.songer (@) songerconsulting.se</span>
                </a>
                <a href="/konsult_cv_juho_ver6.pdf"  // Note: paths from public folder start at root
                   className={`flex items-center gap-2 ${textColor} hover:opacity-80 transition-opacity`}
                   target="_blank" rel="noopener noreferrer">
                  <FileText size={18}/>
                  <span>View Full CV</span>
                </a>
              </div>
            </div>

            {/* Content Column */}
            <div className="md:w-2/3">
              <h2 className="text-2xl font-bold mb-6">
                Juho Songer
                <span className="block text-lg font-normal mt-1 opacity-80">
                Founder and Senior Fullstack Software Engineer
              </span>
              </h2>

              {/* Bio Section */}
              <div className="prose prose-invert max-w-none mb-8">
                <p className="mb-6">
                  Juho is a Senior Software Developer with over 15 years of experience
                  in full-stack development, technical leadership, and database administration.
                </p>
                <p className="mb-6">
                  Juho has an extensive background in Spring Framework, microservices architecture,
                  and secure banking integrations. He has a proven track record in backend development and team
                  collaboration across financial sector projects.
                </p>
              </div>

              {/* Skills Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Technical Expertise</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Full-stack: Java, Spring Boot, React, TypeScript
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Microservices & RESTful API Design
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Database Administration
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Cloud Technologies (AWS, Docker)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Agile & Technical Leadership
                  </li>
                </ul>
              </div>

              {/* Experience Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Industry Experience</h3>
                <p className="mb-4">
                  Led international teams and conducted on-site workshops for clients including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CompanyCard
                      name="Tele2"
                      industry="Telecom"
                      url="https://www.tele2.se/foretag"
                      theme={theme}
                  />
                  <CompanyCard
                      name="Ryder Trucks"
                      industry="Logistics"
                      url="https://www.ryder.com/en-us"
                      theme={theme}
                  />
                  <CompanyCard
                      name="Siemens Healthineers"
                      industry="Healthcare"
                      url="https://www.siemens-healthineers.com/"
                      theme={theme}
                  />
                  <CompanyCard
                      name="Sidel Spa"
                      industry="Manufacturing"
                      url="https://www.sidel.com/en"
                      theme={theme}
                  />
                </div>
              </div>

              {/* Education & Languages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Globe size={20}/>
                    Education
                  </h3>
                  <p>MSc in Computer Science</p>
                  <p className="opacity-70">Mälardalen University, 2005</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Languages size={20} />
                    Languages
                  </h3>
                  <ul className="space-y-2">
                    <li>Swedish (Native)</li>
                    <li>Finnish (Native)</li>
                    <li>English (Full professional proficiency)</li>
                    <li>Italian (Elementary proficiency)</li>
                  </ul>
                </div>
              </div>

              {/* Personal Section */}
              <div className="mt-8 p-6 rounded-lg bg-white/5">
                <h3 className="text-xl font-semibold mb-4">Personal Interests</h3>
                <p>
                  Beyond technology, Juho is an avid learner and recently became a private pilot,
                  constantly expanding his aviation skills through new type ratings. He enjoys
                  traveling with his wife and is currently learning Italian.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-black/20 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Profile Image Column */}
            <div className="md:w-1/3 flex flex-col items-center space-y-4">
              <div className="relative group">
                <img
                    src={profileJesper}
                    alt="Jesper Songer"
                    className="w-64 h-64 rounded-2xl object-cover shadow-lg
            transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 rounded-2xl ring-2 ring-offset-2 ring-offset-black/20
          ring-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
              </div>

              {/* Quick Contact Info */}
              <div className="w-full space-y-3 mt-6">
                <a href="mailto:jesper.songer@songerconsulting.se"
                   className={`flex items-center gap-2 ${textColor} hover:opacity-80 transition-opacity`}>
                  <Mail size={18}/>
                  <span>jesper.songer (@) songerconsulting.se</span>
                </a>
              </div>
            </div>

            {/* Content Column */}
            <div className="md:w-2/3">
              <h2 className="text-2xl font-bold mb-6">
                Jesper Songer
                <span className="block text-lg font-normal mt-1 opacity-80">
          Senior Sales Representative
        </span>
              </h2>

              {/* Bio Section */}
              <div className="prose prose-invert max-w-none mb-8">
                <p className="mb-6">
                  Jesper is a Senior Sales Representative with 15 years of experience in IT and telephony
                  system sales, working with small to large customers across Sweden. He has a proven ability
                  to build strong customer relationships, understand client needs, and deliver tailored
                  solutions that drive business growth.
                </p>
              </div>

              {/* Skills Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Key Expertise</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Complex sales processes and contract negotiation
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Customer relationship management
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Tailored solution development
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Lead generation and sales lifecycle management
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    Consistently exceeding sales targets
                  </li>
                </ul>
              </div>

              {/* Experience Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Industry Experience</h3>
                <p className="mb-4">
                  Extensive experience in IT and telephony sales across leading companies:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CompanyCard
                      name="Merit"
                      industry="IT Solutions"
                      url="https://merit.se/"
                      theme={theme}
                  />
                  <CompanyCard
                      name="Dialect"
                      industry="IT & Telephony"
                      url="https://dialect.se/"
                      theme={theme}
                  />
                  <CompanyCard
                      name="Arsa Data"
                      industry="IT Services"
                      url="https://www.arsadata.se/sv-se/default/"
                      theme={theme}
                  />
                </div>
              </div>

              {/* Professional Summary */}
              <div className="mt-8 p-6 rounded-lg bg-white/5">
                <h3 className="text-xl font-semibold mb-4">Professional Profile</h3>
                <p>
                  Jesper excels at navigating complex sales processes and negotiating contracts with
                  decision-makers at all levels. He is adept at managing the entire sales lifecycle,
                  from lead generation to closing, while consistently exceeding targets. With his
                  extensive experience and customer-focused approach, Jesper is dedicated to driving
                  sales and contributing to business success for both Songer Consulting and its clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Team;